<h2 mat-dialog-title>
  <mat-icon>upload</mat-icon>
  Bulk Import Collectors
</h2>

<mat-dialog-content class="dialog-content">
  <mat-button-toggle-group
    class="mode-toggle"
    [(ngModel)]="mode"
    [disabled]="loading"
    (change)="onModeChanged()"
  >
    <mat-button-toggle value="emails">Email list</mat-button-toggle>
    <mat-button-toggle value="csv">CSV upload</mat-button-toggle>
  </mat-button-toggle-group>

  <section *ngIf="mode === 'emails'" class="import-section">
    <textarea
      rows="7"
      class="form-control bulk-import-input"
      placeholder="john.doe@49ing.ch"
      [(ngModel)]="inputValue"
      [disabled]="loading"
    ></textarea>

    <div *ngIf="emailResult" class="result-panel">
      <h3>Email Import Result</h3>
      <div class="result-grid">
        <div class="result-metric">
          <span>Added</span>
          <strong>{{ emailResult.added }}</strong>
        </div>
        <div class="result-metric">
          <span>Existing</span>
          <strong>{{ emailResult.skippedExisting }}</strong>
        </div>
        <div class="result-metric">
          <span>Not found</span>
          <strong>{{ emailResult.notFound.length }}</strong>
        </div>
        <div class="result-metric">
          <span>Invalid</span>
          <strong>{{ emailResult.invalidEmails.length }}</strong>
        </div>
      </div>
      <p *ngIf="emailResult.notFound.length > 0" class="result-detail">
        Not found: {{ emailResult.notFound.slice(0, 10).join(', ') }}
      </p>
      <p *ngIf="emailResult.invalidEmails.length > 0" class="result-detail">
        Invalid emails:
        {{ emailResult.invalidEmails.slice(0, 10).join(', ') }}
      </p>
    </div>
  </section>

  <section *ngIf="mode === 'csv'" class="import-section">
    <div class="template-section">
      <button
        mat-stroked-button
        color="primary"
        type="button"
        [disabled]="loading"
        (click)="downloadTemplate()"
      >
        <mat-icon>download</mat-icon>
        Download template
      </button>
    </div>

    <div class="file-input-section">
      <input
        #fileInput
        type="file"
        accept=".csv"
        (change)="onFileSelected($event)"
        [disabled]="loading"
        hidden
      />
      <button
        mat-raised-button
        color="primary"
        type="button"
        matTooltip="Max file size: 20MB. Max number of rows: 1000."
        [disabled]="loading"
        (click)="clearSelectedFile(); fileInput.click()"
      >
        <mat-icon>upload_file</mat-icon>
        Upload file
      </button>
      <span *ngIf="fileName" class="file-chip">
        <mat-icon>description</mat-icon>
        {{ fileName }}
      </span>
    </div>

    <div *ngIf="csvPreviewResult" class="result-panel">
      <h3>CSV Preview</h3>
      <ng-container
        [ngTemplateOutlet]="csvResultSummary"
        [ngTemplateOutletContext]="{ result: csvPreviewResult }"
      ></ng-container>

      <div
        *ngIf="
          csvPreviewResult.newTeams.length > 0 ||
          csvPreviewResult.newRoles.length > 0 ||
          csvPreviewResult.discordAliasCollisions.length > 0
        "
        class="preview-warnings"
      >
        <div *ngIf="csvPreviewResult.newTeams.length > 0">
          <h4>New teams</h4>
          <p class="result-detail">
            {{ csvPreviewResult.newTeams.slice(0, 10).join(', ') }}
          </p>
        </div>
        <div *ngIf="csvPreviewResult.newRoles.length > 0">
          <h4>New roles</h4>
          <p class="result-detail">
            {{ csvPreviewResult.newRoles.slice(0, 10).join(', ') }}
          </p>
        </div>
        <div *ngIf="csvPreviewResult.discordAliasCollisions.length > 0">
          <h4>Discord alias collisions</h4>
          <ul class="result-failures">
            <li
              *ngFor="
                let collision of csvPreviewResult.discordAliasCollisions.slice(
                  0,
                  10
                )
              "
            >
              <strong>{{ collision.discordAlias }}:</strong>
              imported {{ formatImportedAliasCollision(collision) }}; existing
              {{ formatExistingAliasCollision(collision) }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div *ngIf="csvResult" class="result-panel">
      <h3>CSV Import Result</h3>
      <ng-container
        [ngTemplateOutlet]="csvResultSummary"
        [ngTemplateOutletContext]="{ result: csvResult }"
      ></ng-container>
    </div>

    <ng-template #csvResultSummary let-result="result">
      <div class="result-grid">
        <div class="result-metric">
          <span>Added</span>
          <strong>{{ result.added }}</strong>
        </div>
        <div class="result-metric">
          <span>Updated</span>
          <strong>{{ result.updated }}</strong>
        </div>
        <div class="result-metric">
          <span>Failed</span>
          <strong>{{ (result.failed ?? []).length }}</strong>
        </div>
        <div class="result-metric">
          <span>Duplicate</span>
          <strong>{{ result.skipped }}</strong>
        </div>
        <div class="result-metric">
          <span>Not found</span>
          <strong>{{ result.notFound.length }}</strong>
        </div>
        <div class="result-metric">
          <span>Invalid rows</span>
          <strong>{{ result.invalid.length }}</strong>
        </div>
      </div>
      <p *ngIf="result.notFound.length > 0" class="result-detail">
        Not found: {{ result.notFound.slice(0, 10).join(', ') }}
      </p>
      <ul *ngIf="(result.failed ?? []).length > 0" class="result-failures">
        <li *ngFor="let failure of (result.failed ?? []).slice(0, 10)">
          <strong>{{ failure.email || failure.auth0Id || 'Unknown' }}:</strong>
          {{ failure.reason }}
        </li>
      </ul>
      <p *ngIf="(result.ignoredColumns ?? []).length > 0" class="result-detail">
        Ignored columns:
        {{ (result.ignoredColumns ?? []).slice(0, 10).join(', ') }}
      </p>
    </ng-template>
  </section>

  <div *ngIf="validationErrors.length > 0" class="validation-errors">
    <h3>Validation Rows</h3>
    <ul>
      <li *ngFor="let err of validationErrors">
        <strong>Row {{ err.row }}, {{ err.column || 'CSV' }}:</strong>
        {{ err.message }}
      </li>
    </ul>
  </div>

  <div *ngIf="error" class="error-message">
    <mat-icon>error</mat-icon>
    {{ error }}
  </div>

  <mat-progress-bar
    *ngIf="loading"
    mode="indeterminate"
    color="primary"
  ></mat-progress-bar>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" [disabled]="loading" (click)="cancel()">
    {{ importCompleted ? 'Close' : 'Cancel' }}
  </button>
  <button
    mat-raised-button
    color="primary"
    type="button"
    [disabled]="!canImport"
    (click)="importCollectors()"
  >
    <mat-icon>{{ importActionIcon }}</mat-icon>
    {{ importActionLabel }}
  </button>
</mat-dialog-actions>
